import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaFileContract } from 'react-icons/fa';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-bold mb-3">{title}</h2>
    <div className="text-sm leading-relaxed space-y-3 text-gray-600">{children}</div>
  </section>
);

const TermsOfUse = () => {
  const navigate = useNavigate();
  const isAuthenticated = !!sessionStorage.getItem('token');

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center space-x-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            data-testid="terms-back-btn"
          >
            <FaArrowLeft className="text-gray-700" />
          </button>
          <div className="flex items-center space-x-2">
            <FaFileContract className="text-[#4A90E2]" />
            <h1 className="text-xl font-bold">Terms of Use</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8" data-testid="terms-of-use-page">
        <p className="text-xs text-gray-400 mb-6">Last updated: February 1, 2026</p>

        <Section title="1. Acceptance of Terms">
          <p>
            These Terms of Use (&quot;Terms&quot;) govern your access to and use of TalkinPoint, operated by GDM. By creating an account or using any part of the platform, you agree to be bound by these Terms. If you do not agree to these Terms, do not use TalkinPoint.
          </p>
        </Section>

        <Section title="2. Eligibility">
          <p>You must be at least 13 years of age to use TalkinPoint. If you are between 13 and 18, you must have parental or guardian consent. By using the platform, you represent that you meet these eligibility requirements.</p>
        </Section>

        <Section title="3. Your Account">
          <p>You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account. You agree to:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Provide accurate and complete registration information</li>
            <li>Keep your password secure and not share it with others</li>
            <li>Notify us immediately of any unauthorized use of your account</li>
            <li>Not create accounts for the purpose of impersonation or deception</li>
          </ul>
          <p>GDM reserves the right to suspend or terminate accounts that violate these Terms.</p>
        </Section>

        <Section title="4. User Content">
          <p><strong>Ownership:</strong> You retain ownership of the content you post on TalkinPoint, including text, images, videos, audio, polls, and PointClips. By posting content, you grant GDM a worldwide, non-exclusive, royalty-free license to use, display, reproduce, distribute, and modify your content in connection with operating and promoting the platform.</p>
          <p><strong>Responsibility:</strong> You are solely responsible for the content you post. You represent that you have the right to share any content you upload and that it does not infringe on the rights of any third party.</p>
          <p><strong>Cross-Posting:</strong> When you use TalkinPoint's cross-posting features to publish content to external platforms (X/Twitter, Facebook, Instagram, LinkedIn, TikTok, YouTube), you acknowledge that such content becomes subject to the terms and policies of those respective platforms.</p>
        </Section>

        <Section title="5. Prohibited Conduct">
          <p>You agree not to:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Post content that is illegal, harmful, threatening, abusive, harassing, defamatory, vulgar, obscene, or otherwise objectionable</li>
            <li>Engage in hate speech, discrimination, or targeted harassment of individuals or groups</li>
            <li>Share sexually explicit content involving minors</li>
            <li>Impersonate any person or entity, or falsely claim affiliation</li>
            <li>Use automated bots, scrapers, or scripts to access TalkinPoint without prior written consent</li>
            <li>Spam, solicit, or distribute malware through the platform</li>
            <li>Attempt to gain unauthorized access to other users' accounts or platform systems</li>
            <li>Manipulate the Point of the Day (POTD) system, PointArena votes, or engagement metrics through artificial means</li>
            <li>Use cross-posting features to distribute prohibited content across connected platforms</li>
            <li>Interfere with or disrupt the platform's infrastructure or other users' experience</li>
          </ul>
        </Section>

        <Section title="6. PointArena & POTD">
          <p><strong>PointArena:</strong> Debates on PointArena are subject to community guidelines. Arguments must be made in good faith. Vote manipulation, brigading, and abusive debate tactics are prohibited and may result in arena bans.</p>
          <p><strong>Point of the Day (POTD):</strong> POTD winners are determined by engagement metrics. GDM reserves the right to disqualify entries that violate platform rules or employ artificial engagement tactics.</p>
        </Section>

        <Section title="7. Live Streaming (PointLive)">
          <p>When using PointLive, you agree to comply with all applicable laws regarding live broadcasting. You must not stream content that violates these Terms or is illegal in your jurisdiction. GDM reserves the right to terminate live streams that violate our policies without prior notice.</p>
        </Section>

        <Section title="8. Verification">
          <p>TalkinPoint's verification program is administered at GDM's sole discretion. Submitting a verification request does not guarantee approval. Verified status may be revoked if an account violates these Terms or misrepresents its identity.</p>
        </Section>

        <Section title="9. Creators Hub & Monetization">
          <p>Creator programs (Starter, Pro, Elite) are subject to separate creator agreements and payment terms. GDM reserves the right to modify creator program features, benefits, and pricing with reasonable notice. Revenue sharing, tips, and payouts are subject to applicable tax laws and reporting requirements.</p>
        </Section>

        <Section title="10. Intellectual Property">
          <p>The TalkinPoint name, logo, design, and all associated branding are the intellectual property of GDM. You may not use our trademarks without prior written permission. The platform's software, code, and proprietary technology are protected by intellectual property laws.</p>
        </Section>

        <Section title="11. Account Suspension & Termination">
          <p><strong>Voluntary Suspension:</strong> You may suspend your account at any time through Settings. Suspended accounts can be reactivated by logging back in.</p>
          <p><strong>Voluntary Deletion:</strong> You may permanently delete your account through Settings. This action is irreversible and will remove all your data within 30 days.</p>
          <p><strong>GDM-Initiated Actions:</strong> We may suspend, restrict, or terminate your account if you violate these Terms, engage in prohibited conduct, or if required by law. We will provide notice when reasonably possible.</p>
        </Section>

        <Section title="12. Third-Party Services">
          <p>TalkinPoint integrates with third-party social media platforms for cross-posting and may use third-party services for payments, analytics, and content delivery. Your interactions with these services are governed by their respective terms and privacy policies. GDM is not responsible for the practices of third-party services.</p>
        </Section>

        <Section title="13. Disclaimers">
          <p>TalkinPoint is provided on an &quot;as is&quot; and &quot;as available&quot; basis. GDM makes no warranties, express or implied, regarding the platform's reliability, availability, or fitness for a particular purpose. We do not guarantee uninterrupted or error-free service.</p>
        </Section>

        <Section title="14. Limitation of Liability">
          <p>To the maximum extent permitted by law, GDM shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of your use of TalkinPoint, including but not limited to loss of data, revenue, or business opportunities.</p>
        </Section>

        <Section title="15. Indemnification">
          <p>You agree to indemnify and hold GDM, its officers, directors, employees, and agents harmless from any claims, damages, or expenses arising from your use of TalkinPoint, your content, or your violation of these Terms.</p>
        </Section>

        <Section title="16. Governing Law">
          <p>These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which GDM is incorporated, without regard to conflict of law principles.</p>
        </Section>

        <Section title="17. Changes to Terms">
          <p>GDM reserves the right to modify these Terms at any time. We will notify users of material changes through the platform or via email. Continued use of TalkinPoint after changes take effect constitutes acceptance of the revised Terms.</p>
        </Section>

        <Section title="18. Contact">
          <p>For questions about these Terms of Use, contact us:</p>
          <p><strong>GDM</strong><br />Email: legal@gdmhq.com<br />Website: <a href="https://www.gdmhq.com" target="_blank" rel="noopener noreferrer" className="text-[#4A90E2] hover:underline">www.gdmhq.com</a></p>
        </Section>

        {/* Footer nav */}
        <div className="mt-12 pt-6 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
          <span>GDM - TalkinPoint</span>
          <div className="flex space-x-4">
            <Link to="/privacy" className="hover:text-[#4A90E2] transition-colors" data-testid="terms-to-privacy-link">Privacy Policy</Link>
            {isAuthenticated && <Link to="/settings" className="hover:text-[#4A90E2] transition-colors">Settings</Link>}
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsOfUse;
